import { toDate } from './dates';
import { isMediablockConnected } from './mediablock';

// Removing a DCP from the Mediablock is a two-system flow. bac-app only *marks* the content
// (POST /content/:id/mark-deleted -> isDeleted + deletedAt); the actual
// `remove-cpl-from-player` DELETE is done by the bac-python cronjob at the end of every
// discovery pass, which then stamps the progress fields we read back here.
//
// These two constants mirror that worker (dcp_parse.py: FAILED_REMOVE_RETRY_AFTER and
// MAX_REMOVE_FAILURE_ATTEMPTS). Keep them in sync if the worker's retry policy changes —
// they are only used to tell the user when the next attempt is due.
export const MAX_REMOVE_ATTEMPTS = 3;
export const RETRY_AFTER_MINUTES = 15;

// The worker claims a row with mediablockRemoveInProgressAt and releases it within the same
// pass, so this marker should only ever be visible for seconds. Nothing expires the claim:
// an older one means the run died mid-delete and the row will never be picked up again.
const STALE_CLAIM_MINUTES = 30;

export const TONE_BADGE_CLASS = {
    info: 'bg-info text-dark',
    warning: 'bg-warning text-dark',
    danger: 'bg-danger text-white',
};

export const TONE_TEXT_CLASS = {
    info: 'text-info',
    warning: 'text-warning',
    danger: 'text-danger',
};

/**
 * Single source of truth for "what is happening to this content's deletion", shared by the
 * content card badge, the CPL modal and the Mediablock status bar so the three can't drift.
 *
 * Returns null when there is nothing to show: not marked, already removed (those rows are
 * filtered out server-side anyway), or a BAC server row — `isDeleted` there comes from the
 * disk FIFO, not from the Mediablock cleanup job, and this status would be a lie.
 */
export const getDeletionState = (content) => {
    if (!content || content.location !== 'mediablock') return null;
    if (!content.isDeleted || content.mediablockRemovedAt) return null;

    // mediablockRemoveFailureCount / mediablockRemoveAbandonedAt are written by bac-python but
    // are not declared on the Node Content schema. They still reach us in the JSON response.
    const attempts = content.mediablockRemoveFailureCount || 0;
    const base = {
        requestedAt: toDate(content.deletedAt),
        inProgressAt: toDate(content.mediablockRemoveInProgressAt),
        failedAt: toDate(content.mediablockRemoveFailedAt),
        lastAttemptAt: toDate(content.mediablockRemoveLastAttemptAt),
        abandonedAt: toDate(content.mediablockRemoveAbandonedAt),
        attempts,
        attemptsAllowed: MAX_REMOVE_ATTEMPTS,
        error: content.mediablockRemoveError || '',
        nextRetryAt: null,
        needsAttention: false,
    };

    // The cleanup job only ever claims CPLs, so a KDM marked here sits untouched forever.
    if (content.type !== 'CPL') {
        return {
            ...base,
            state: 'unsupported',
            needsAttention: true,
            tone: 'danger',
            icon: 'fa-exclamation-triangle',
            label: 'Needs manual removal',
            detail: 'Only DCPs are removed automatically. This file has to be taken off the Mediablock by hand.',
        };
    }

    if (base.abandonedAt || attempts >= MAX_REMOVE_ATTEMPTS) {
        return {
            ...base,
            state: 'failed',
            needsAttention: true,
            tone: 'danger',
            icon: 'fa-exclamation-circle',
            label: 'Removal failed',
            detail: `The Mediablock refused the removal ${attempts || MAX_REMOVE_ATTEMPTS} times, so the cleanup job stopped trying. It will not retry on its own.`,
        };
    }

    if (base.inProgressAt && Date.now() - base.inProgressAt.getTime() > STALE_CLAIM_MINUTES * 60000) {
        return {
            ...base,
            state: 'stalled',
            needsAttention: true,
            tone: 'danger',
            icon: 'fa-clock',
            label: 'Removal stalled',
            detail: 'A removal was started but never finished — the cleanup job most likely stopped mid-run.',
        };
    }

    if (base.inProgressAt) {
        return {
            ...base,
            state: 'removing',
            tone: 'info',
            icon: 'fa-spinner',
            label: 'Removing…',
            detail: 'The cleanup job is removing this DCP from the Mediablock right now.',
        };
    }

    if (base.failedAt) {
        const attempt = attempts || 1;
        return {
            ...base,
            state: 'retrying',
            tone: 'warning',
            icon: 'fa-rotate-right',
            nextRetryAt: new Date(base.failedAt.getTime() + RETRY_AFTER_MINUTES * 60000),
            label: `Retrying (${attempt} of ${MAX_REMOVE_ATTEMPTS})`,
            detail: `Attempt ${attempt} of ${MAX_REMOVE_ATTEMPTS} failed. The cleanup job waits ${RETRY_AFTER_MINUTES} minutes between attempts.`,
        };
    }

    return {
        ...base,
        state: 'queued',
        tone: 'warning',
        icon: 'fa-trash',
        label: 'Queued for removal',
        detail: 'Marked for deletion. The cleanup job removes it from the Mediablock on its next pass.',
    };
};

// A DCP can only be marked for deletion once, and only DCPs are ever removed automatically. The
// cleanup job does not check the player before calling the TMS, so while the Mediablock is not
// connected the request would fail three times and be abandoned — the server refuses it (409) and
// the button says so up front.
export const canMarkForDeletion = (content, organization) =>
    content?.location === 'mediablock' && content?.type === 'CPL' && !content?.isDeleted &&
    isMediablockConnected(organization?.mediablock);

// Only removals nothing will ever finish can be cleared: the job gave up, a claim was never released,
// or it is a KDM the job never claims. Anything in flight is left to the job — restoring it would race
// the local<->cloud sync and the mark would come straight back.
export const canRestoreDeletion = (content) => getDeletionState(content)?.needsAttention === true;
